import { Button, Link, MenuItem, OutlinedInput, Radio, Select, Stack } from "@mui/material";
import { forwardRef, useEffect, useMemo, useState } from "react";
import { Language } from "../../../Core/Api/Types/language";
import { PlatformLanguageCode } from "../../../Core/Api/Types/platform-language-code";
import DataTable, { CellSize } from "../../Table/table";
import { MetadataInputProps } from "../metadata-input-index";
import CheckIcon from "@mui/icons-material/Check";
import WarningIcon from "@mui/icons-material/Warning";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { GridActionsCellItem, GridColDef, GridRowParams, GridRowsProp } from "@mui/x-data-grid-premium";
import RemoveIcon from "@mui/icons-material/Remove";
import MoreMenuFieldWrapper from "../../Table/MoreMenuFieldWrapper/more-menu-field-wrapper";
import { GridRenderEditCellParams, useGridApiContext } from "@mui/x-data-grid-pro";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useTranslation } from "react-i18next";

enum CastFunction {
    NotDefined = "NotDefined",
    Director = "Director",
    CoDirector = "CoDirector",
    Producer = "Producer",
    Screenwriter = "Screenwriter",
    Composer = "Composer",
    Actor = "Actor",
}

export type CastMetadataInfos = {
    order: number;
    function: string;
    functionEnum: CastFunction | undefined;
    name: string;
    lastname: string;
    charName: string;
    languageCode: PlatformLanguageCode;
    languageLocalized: Language;
    languageLocalizedCultureId: number | undefined;
    epNb?: number | null;
};

type CastMetadataInfosIndexed = {
    index: number;
    data: CastMetadataInfos;
};

const MetadataInputCasts = forwardRef<any, MetadataInputProps>((props, ref) => {
    const { t } = useTranslation();
    const {
        onChange,
        data,
        value,
        cultureId,
        onRemoveRow,
        onAddRow,
        onDuplicateRows,
        availableLanguages,
        onUpdateAllRows,
        onUpdateRowsBelow,
        onReset,
        onOrderCastRow,
        epNb,
    } = props;

    const getLocalizedValues = (): CastMetadataInfosIndexed[] => {
        let array: CastMetadataInfosIndexed[] = [];

        if (value && value.length) {
            if (!cultureId)
                return (value as CastMetadataInfos[])
                    .filter((x) => x.languageLocalizedCultureId === null && x.epNb === epNb && x.order !== 9999)
                    .map((data, index) => ({
                        index: index,
                        data: {
                            ...data,
                            status:
                                data.lastname && data.name && data.function && data.function !== CastFunction.NotDefined
                                    ? "complete"
                                    : "",
                        },
                    }));

            for (let i = 0; i < (value as CastMetadataInfos[]).length; ++i) {
                if (
                    value[i].languageLocalizedCultureId &&
                    value[i].languageLocalizedCultureId === cultureId &&
                    value[i].epNb === epNb &&
                    value[i].order !== 9999
                )
                    array.push({
                        index: i,
                        data: {
                            ...value[i],
                            status:
                                value[i].lastname && value[i].name && value[i].function && value[i].function !== CastFunction.NotDefined
                                    ? "complete"
                                    : "",
                        },
                    });
            }
        }
        return array;
    };

    useEffect(() => {
        setSelectedRows([]);
    }, [cultureId]);

    const [selectedRows, setSelectedRows] = useState<number[]>([]);

    const handleSelectedRow = (e: any, index: number) => {
        if (selectedRows.find((x) => x === index) !== undefined) {
            setSelectedRows((x) => x.filter((y) => y !== index));
        } else {
            setSelectedRows((x) => [...x, index]);
        }
    };

    const localized = useMemo((): CastMetadataInfosIndexed[] => {
        return getLocalizedValues();
    }, [value, cultureId, epNb]);

    const castRows: GridRowsProp = localized;
    type Row = (typeof castRows)[number];

    const getCastColumns = () =>
        availableLanguages ? [castColumns[0], languageColumn, ...castColumns.slice(1)] : castColumns;

    const languageColumn: GridColDef = {
        field: "language",
        headerName: t("common:language"),
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        editable: true,
        valueGetter: (params: any, row: any) => row?.data.languageLocalized,
        valueFormatter: (params: any, row: any) => params?.englishName,
        renderEditCell: (d: GridRenderEditCellParams) => (
            <CustomEditCell
                availableLanguages={availableLanguages}
                d={d}
                data={data}
                onChange={onChange}
                onReset={onReset}
                onUpdateAllRows={onUpdateAllRows}
                onUpdateRowsBelow={onUpdateRowsBelow}
            />
        ),
    };

    const castColumns: GridColDef[] = [
        {
            field: "status",
            headerName: t("common:status"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            valueGetter: (params: any, row: any) => row?.data.status,
            renderCell: (data: Row) => {
                switch (data?.row?.data.status) {
                    case "complete":
                        return <CheckIcon fontSize="small" color="success" />;
                    default:
                        return <WarningIcon fontSize="small" color="warning" />;
                }
            },
        },
        {
            field: "functionEnum",
            headerName: t("common:function"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            editable: true,
            valueGetter: (params: any, row: any) => row?.data.functionEnum,
            renderEditCell: (d: GridRenderEditCellParams) => (
                <CustomEditCell
                    availableLanguages={availableLanguages}
                    d={d}
                    data={data}
                    onChange={onChange}
                    onReset={onReset}
                    onUpdateAllRows={onUpdateAllRows}
                    onUpdateRowsBelow={onUpdateRowsBelow}
                />
            ),
        },
        {
            field: "name",
            headerName: t("common:firstName"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            editable: true,
            valueGetter: (params: any, row: any) => row?.data.name,
            renderEditCell: (d: GridRenderEditCellParams) => (
                <CustomEditCell
                    availableLanguages={availableLanguages}
                    d={d}
                    data={data}
                    onChange={onChange}
                    onReset={onReset}
                    onUpdateAllRows={onUpdateAllRows}
                    onUpdateRowsBelow={onUpdateRowsBelow}
                />
            ),
        },
        {
            field: "lastname",
            headerName: t("common:lastName"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            editable: true,
            valueGetter: (params: any, row: any) => row?.data.lastname,
            renderEditCell: (d: GridRenderEditCellParams) => (
                <CustomEditCell
                    availableLanguages={availableLanguages}
                    d={d}
                    data={data}
                    onChange={onChange}
                    onReset={onReset}
                    onUpdateAllRows={onUpdateAllRows}
                    onUpdateRowsBelow={onUpdateRowsBelow}
                />
            ),
        },
        {
            field: "charName",
            headerName: t("table:headerName.characterName"),
            flex: CellSize.medium,
            minWidth: CellSize.medium,
            editable: true,
            valueGetter: (params: any, row: any) => row?.data.charName,
            renderEditCell: (d: GridRenderEditCellParams) => (
                <CustomEditCell
                    availableLanguages={availableLanguages}
                    d={d}
                    data={data}
                    onChange={onChange}
                    onReset={onReset}
                    onUpdateAllRows={onUpdateAllRows}
                    onUpdateRowsBelow={onUpdateRowsBelow}
                />
            ),
        },
        {
            field: "actions",
            type: "actions",
            align: "right",
            flex: onDuplicateRows ? (onRemoveRow ? 1 : 0.5) : 0,
            getActions: (params: GridRowParams) => {
                if (!params || !params.row) return [];
                const actions: any[] = [];
                if (onOrderCastRow && params?.row?.index != 0)
                    actions.push(
                        <GridActionsCellItem
                            icon={<ArrowUpwardIcon />}
                            label={t("common:lowerCaseUp")}
                            onClick={() => onOrderCastRow(params?.row?.index - 1, data.id, params?.row?.index)}
                        />
                    );
                if (onOrderCastRow && params?.row?.index != castRows.length - 1)
                    actions.push(
                        <GridActionsCellItem
                            icon={<ArrowDownwardIcon />}
                            label={t("common:down")}
                            onClick={() => onOrderCastRow(params?.row?.index + 1, data.id, params?.row?.index)}
                        />
                    );

                onDuplicateRows &&
                    actions.push(
                        <Radio
                            value={params?.row?.index}
                            checked={selectedRows.find((x) => x === params?.row?.index) !== undefined ? true : false}
                            onClick={(e) => handleSelectedRow(e, params?.row?.index)}
                        />
                    );
                onRemoveRow &&
                    actions.push(
                        <GridActionsCellItem
                            icon={<RemoveIcon />}
                            label={t("table:cellLabel.removeCastRow")}
                            onClick={() => onRemoveRow(params?.row?.index)}
                        />
                    );
                return actions;
            },
        },
    ];

    return (
        <>
            <DataTable
                noControls
                editMode="cell"
                rows={castRows}
                columns={getCastColumns()}
                getRowId={(row: any) => row.index}
                pageSize={999}
                columnHeaderHeight={48}
                rowHeight={56}
                hideFooter
                disableRowSelectionOnClick
            />

            <Stack spacing={2} marginTop="15px" width="100%" display="flex" justifyContent="flex-end" direction="row">
                {onDuplicateRows && (
                    <>
                        <Link underline="hover" onClick={() => setSelectedRows(castRows.map((x, i) => x.index))}>
                            {t("common:selectAll")}
                        </Link>
                        <Link underline="hover" onClick={() => setSelectedRows([])}>
                            {t("common:deselectAll")}
                        </Link>
                        <Button
                            disabled={selectedRows.length === 0}
                            sx={{ width: "150px" }}
                            variant="outlined"
                            onClick={() => {
                                onDuplicateRows(selectedRows);
                                setSelectedRows([]);
                            }}
                        >
                            {t("button:duplicateRows")}
                        </Button>
                    </>
                )}
                {onAddRow && (
                    <Button sx={{ width: "150px" }} variant="outlined" onClick={onAddRow}>
                        {t("button:addRow")}
                    </Button>
                )}
            </Stack>
        </>
    );
});

type CustomEditCellProps = {
    data: DbMetadataFieldInfos;
    d: GridRenderEditCellParams;
    onChange: any;
    onUpdateRowsBelow: any;
    onReset: any;
    onUpdateAllRows: any;
    availableLanguages: any[] | undefined;
};

function CustomEditCell(props: CustomEditCellProps) {
    const { data, d, onChange, onReset, onUpdateAllRows, onUpdateRowsBelow, availableLanguages } = props;
    const apiRef = useGridApiContext();

    switch (d.colDef.field) {
        case "functionEnum":
            return (
                <>
                    <Select
                        displayEmpty
                        IconComponent={ExpandMoreRoundedIcon}
                        value={d.value}
                        inputProps={{ "aria-label": "Without label" }}
                        size="small"
                        sx={{ flex: 1 }}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                        onChange={(e) => {
                            onChange(
                                { ...d.row.data, functionEnum: e.target.value, function: e.target.value as CastFunction },
                                data.id,
                                d.row.index
                            );
                            apiRef.current.setEditCellValue({ id: d.id, field: d.field, value: e.target.value });
                        }}
                    >
                        {Object.keys(CastFunction).map((func, i) => (
                            <MenuItem key={i} value={func}>
                                {func}
                            </MenuItem>
                        ))}
                    </Select>
                    <MoreMenuFieldWrapper
                        field="functionEnum"
                        onUpdateRowsBelow={onUpdateRowsBelow}
                        onReset={onReset}
                        onUpdateAllRows={onUpdateAllRows}
                        value={d.value}
                        index={d.row.index}
                    />
                </>
            );
        case "language":
            return (
                <>
                    {availableLanguages && (
                        <>
                            <Select
                                displayEmpty
                                IconComponent={ExpandMoreRoundedIcon}
                                value={d.value.id as Language}
                                label={d.value.englishName}
                                inputProps={{ "aria-label": "Without label" }}
                                size="small"
                                sx={{ flex: 1, maxWidth: "85%" }}
                                onChange={(e) => {
                                    onChange(
                                        { ...d.row.data, languageLocalized: availableLanguages.find((x) => x.id === e.target.value) },
                                        data.id,
                                        d.row.index
                                    );
                                    apiRef.current.setEditCellValue({
                                        id: d.id,
                                        field: d.field,
                                        value: availableLanguages.find((x) => x.id === e.target.value),
                                    });
                                }}
                            >
                                {availableLanguages.map((l: Language, i) => (
                                    <MenuItem key={i} value={l.id!}>
                                        {l.englishName?.toString() || ""}
                                    </MenuItem>
                                ))}
                            </Select>
                            <MoreMenuFieldWrapper
                                field="language"
                                onUpdateRowsBelow={onUpdateRowsBelow}
                                onReset={onReset}
                                onUpdateAllRows={onUpdateAllRows}
                                value={d.value as Language}
                                index={d.row.index}
                            />
                        </>
                    )}
                </>
            );
        case "name":
            return (
                <>
                    <OutlinedInput
                        value={d.value}
                        sx={{ flex: 1 }}
                        size="small"
                        onKeyDown={(e) => e.stopPropagation()}
                        onBlur={(e) => {
                            e.target &&
                                e.target.value &&
                                onChange({ ...d.row.data, name: e.target.value.trim() }, data.id, d.row.index);
                        }}
                        onChange={(e) => {
                            onChange({ ...d.row.data, name: e.currentTarget.value }, data.id, d.row.index);
                            apiRef.current.setEditCellValue({ id: d.id, field: d.field, value: e.target.value });
                        }}
                    />
                    <MoreMenuFieldWrapper
                        field="name"
                        onUpdateRowsBelow={onUpdateRowsBelow}
                        onReset={onReset}
                        onUpdateAllRows={onUpdateAllRows}
                        value={d.value}
                        index={d.row.index}
                    />
                </>
            );
        case "lastname":
            return (
                <>
                    <OutlinedInput
                        value={d.value}
                        sx={{ flex: 1 }}
                        size="small"
                        onKeyDown={(e) => e.stopPropagation()}
                        onBlur={(e) => {
                            e.target &&
                                e.target.value &&
                                onChange({ ...d.row.data, lastname: e.target.value.trim() }, data.id, d.row.index);
                        }}
                        onChange={(e) => {
                            onChange({ ...d.row.data, lastname: e.currentTarget.value }, data.id, d.row.index);
                            apiRef.current.setEditCellValue({ id: d.id, field: d.field, value: e.target.value });
                        }}
                    />
                    <MoreMenuFieldWrapper
                        field="lastname"
                        onUpdateRowsBelow={onUpdateRowsBelow}
                        onReset={onReset}
                        onUpdateAllRows={onUpdateAllRows}
                        value={d.value}
                        index={d.row.index}
                    />
                </>
            );
        case "charName":
            return (
                <>
                    <OutlinedInput
                        value={d.value}
                        size="small"
                        sx={{ flex: 1 }}
                        onKeyDown={(e) => e.stopPropagation()}
                        onBlur={(e) => {
                            e.target &&
                                e.target.value &&
                                onChange({ ...d.row.data, charName: e.target.value.trim() }, data.id, d.row.index);
                        }}
                        onChange={(e) => {
                            onChange({ ...d.row.data, charName: e.currentTarget.value }, data.id, d.row.index);
                            apiRef.current.setEditCellValue({ id: d.id, field: d.field, value: e.target.value });
                        }}
                    />
                    <MoreMenuFieldWrapper
                        field="charName"
                        onUpdateRowsBelow={onUpdateRowsBelow}
                        onReset={onReset}
                        onUpdateAllRows={onUpdateAllRows}
                        value={d.value}
                        index={d.row.index}
                    />
                </>
            );
        default:
            return <></>;
    }
}

export default MetadataInputCasts;
